// external imports
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';

// internal imports
import Layout from 'Component/Layouts/layout';
import { useAssignments, needsAction } from 'contexts/AssignmentsContext';
import { StatusPill } from './common';

const MyAssignments = () => {
  const navigate = useNavigate();
  const { assignments, loading, refresh } = useAssignments();

  // pull a fresh copy when the screen opens
  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // only the assignments waiting on the translator (pending / sent back)
  const visible = React.useMemo(() => assignments.filter(needsAction), [assignments]);

  // row click → per-assignment task page (built separately)
  const openAssignment = (a) => {
    const taskType = a.task?.taskType || 'task';
    const taskId = a.task?._id;
    if (!taskId) return;
    navigate(`/my-assignment/${taskType}/${taskId}`);
  };

  const gridTemplate = '2.4fr 1fr 1.2fr 1fr 0.6fr';

  const headerCellSx = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#94a3b8',
    textTransform: 'uppercase',
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
            My Assignments
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Tasks waiting on you. Open an assignment to view its details and submit your work.
          </Typography>
        </Box>

        {/* table card */}
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {loading && assignments.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : visible.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                You're all caught up
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 14 }}>
                No translations are waiting on you right now.
              </Typography>
            </Box>
          ) : (
            <>
              {/* header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  alignItems: 'center',
                  gap: 1,
                  px: 3,
                  py: 2,
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <Typography sx={headerCellSx}>Task Name</Typography>
                <Typography sx={headerCellSx}>Type</Typography>
                <Typography sx={headerCellSx}>Language</Typography>
                <Typography sx={headerCellSx}>Status</Typography>
                <Typography sx={{ ...headerCellSx, textAlign: 'right' }} />
              </Box>

              {/* rows */}
              {visible.map((a, index) => (
                <Box
                  key={a._id}
                  onClick={() => openAssignment(a)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    alignItems: 'center',
                    gap: 1,
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    borderBottom: index === visible.length - 1 ? 'none' : '1px solid #f1f5f9',
                    '&:hover': { background: '#fafbff' },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14.5, pr: 1 }}>
                    {a.task?.taskName || 'Task'}
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 14, textTransform: 'capitalize' }}>
                    {a.task?.taskType || '—'}
                  </Typography>
                  <Box>
                    <Chip
                      label={a.languageName}
                      size="small"
                      sx={{ background: '#eef2ff', color: '#4338ca', fontWeight: 600 }}
                    />
                  </Box>
                  <Box>
                    <StatusPill status={a.status} />
                  </Box>
                  <Typography
                    onClick={(e) => {
                      e.stopPropagation();
                      openAssignment(a);
                    }}
                    sx={{
                      color: '#4f46e5',
                      fontSize: 14,
                      fontWeight: 600,
                      textAlign: 'right',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Open
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default MyAssignments;
